import { useEffect } from 'react'
import s from './AddUser.scss'
import { Button, Col, Row } from 'reactstrap'
import { KeyValuePairs, InputContainer, Checkbox, Spinner } from 'components/ideal-core-ui'
import Select from 'react-select'
import { toDepartments, fromUserDetails } from '../../serializers'
import { getCurrency, getTenant, getUser, refreshToken } from 'utils/authService'
import { NumericFormat } from 'react-number-format'
import * as Yup from 'yup'
import currency from 'currency.js'
import { useNavigate } from 'react-router-dom'
import { makeGetRequest, makePostRequest, makePutRequest } from 'utils/api'
import { getFormValidationError } from 'utils/formatting'
import update from 'immutability-helper'
import queryString from 'query-string'
import { toTeams } from 'utils/serializers'
import { useImmer } from 'use-immer'
import { useDispatch } from 'react-redux'

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  validateRoles: [
    {
      label: 'User',
      description:
        'Can create/edit opportunities, proposals, contacts, companies; view reports, calendar & all normal functionality',
      value: 'ROLE_USER',
      checked: true
    }
  ],
  roles: [
    {
      label: 'User',
      description:
        'Can create/edit opportunities, proposals, contacts, companies; view reports, calendar & all normal functionality',
      value: 'ROLE_USER',
      checked: true
    },
    {
      label: 'Proposal Reviewer',
      description:
        'In addition to what a User can do, a Proposal Reviewer can Review and Approve proposals',
      value: 'ROLE_PROPOSALREVIEWER',
      checked: false
    },
    {
      label: 'Administrator',
      description:
        'In addition to what a User and Proposal Reviewer can do, an Administrator can add/remove users, edit organization details, create teams & roles',
      value: 'ROLE_ADMIN',
      checked: false
    }
  ],
  teams: [],
  phone: '',
  department: '',
  cellPhone: '',
  organizationId: '',
  monthlyQuota: '',
  yearlyQuota: '',
  monthlyTargetProfit: '',
  yearlyTargetProfit: ''
}

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required!'),
  firstName: Yup.string().required('First Name field is required!'),
  lastName: Yup.string().required('Last Name field is required!'),
  password: Yup.string().required('Password field is required!'),
  validateRoles: Yup.array().required('At least one role needs to be checked').min(1)
})

const editValidationSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name field is required!'),
  lastName: Yup.string().required('Last Name field is required!'),
  validateRoles: Yup.array().required('At least one role needs to be checked').min(1)
})

const AddUser = ({
  departments,
  isEdit,
  organizationId,
  setLoading,
  getSubscriptionInfo,
  error,
  setError,
  loading,
  users,
  setUsers,
  setIsEdit,
  setFormIsVisible,
  teams,
  editedUserDetails
}) => {
  const navigate = useNavigate()
  const [userDetails, setUserDetails] = useImmer(initialState)
  const loggedInUser = getUser()
  const dispatch = useDispatch()

  useEffect(() => {
    setUserDetails(state => {
      state.validateRoles = state.roles.filter(role => role.checked)
      state.organizationId = organizationId
    })
  }, [setUserDetails, userDetails.roles, organizationId])

  useEffect(() => {
    if (editedUserDetails) {
      setUserDetails(() => editedUserDetails)
    }
  }, [editedUserDetails, setUserDetails])

  const updateUser = () => {
    editValidationSchema
      .validate(userDetails)
      .then(async function () {
        setLoading(true)
        const response = await makePutRequest(
          `/v1/users/${userDetails.id}`,
          fromUserDetails(userDetails)
        )
        resetForm()
        const index = users.findIndex(u => u.id === response.user.id)

        const newState = update(users, {
          [index]: {
            $set: {
              ...response.user
            }
          }
        })
        setUsers(newState)
        setUserDetails(() => {
          return { ...initialState, organizationId: organizationId }
        })
        setLoading(false)

        if (userDetails.id === loggedInUser.id) {
          refreshToken()
          dispatch({ type: 'NOTIFICATIONS_FETCH_REQUESTED' })
        }
      })
      .catch(function (err) {
        setError(getFormValidationError(err))
        setLoading(false)
      })
  }

  const resetForm = () => {
    setIsEdit(false)
    setFormIsVisible(false)
    setError('')
    setUserDetails(() => {
      return { ...initialState, organizationId: organizationId }
    })
  }

  const addUser = () => {
    validationSchema
      .validate(userDetails)
      .then(async function () {
        setLoading(true)

        const { email } = userDetails

        const masterSchemaHeader = {
          'x-tenant-schema': 'master'
        }
        const emailExists = await makeGetRequest('/v1/tenants/email', {
          params: {
            email
          },
          headers: masterSchemaHeader
        })

        if (!emailExists) {
          // create tenant
          await makePostRequest(
            '/v1/tenants/create',
            {
              tenantId: getTenant(),
              username: email
            },
            {
              headers: masterSchemaHeader
            }
          )

          const response = await makePostRequest('/v1/users/signup', fromUserDetails(userDetails))
          setFormIsVisible(false)
          setError('')
          setUsers([...users, response.user])
          setUserDetails(() => {
            return { ...initialState, organizationId: organizationId }
          })
          getSubscriptionInfo()
          setLoading(false)
        } else {
          setError('Email already exists')
          setLoading(false)
        }
        const search = queryString.parse(location.search)
        if (search.stripe) {
          delete search.stripe
          navigate({ search: queryString.stringify(search) })
        }
      })
      .catch(function (err) {
        setError(getFormValidationError(err))
        setLoading(false)
      })
  }

  const changeProperty = (value, property) => {
    setUserDetails(state => {
      state[property] = value
    })
  }

  const changeQuota = (value, property) => {
    setUserDetails(state => {
      state[property] = currency(value).value
      if (property === 'monthlyQuota') {
        state.yearlyQuota = currency(value).multiply(12).value
      } else {
        state.monthlyQuota = currency(value).divide(12).value
      }
    })
  }

  const changeTargetProfit = (value, property) => {
    setUserDetails(state => {
      state[property] = currency(value).value
      if (property === 'monthlyTargetProfit') {
        state.yearlyTargetProfit = currency(value).multiply(12).value
      } else {
        state.monthlyTargetProfit = currency(value).divide(12).value
      }
    })
  }

  const changeRoles = (value, index) => {
    setUserDetails(state => {
      state.roles[index].checked = value
    })
  }

  return (
    <div className={s.formWrapper}>
      <Col xl="12" className={s.form}>
        <Row>
          <Col lg="6">
            <KeyValuePairs
              pairs={[
                {
                  key: '* First Name',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <InputContainer
                      type="text"
                      placeholder="Enter First Name"
                      cb={value => changeProperty(value, 'firstName')}
                      text={userDetails.firstName}
                      cy="user_first_name_input"
                    />
                  )
                },
                {
                  key: '* Email',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <InputContainer
                      type="email"
                      placeholder="Enter Email"
                      cb={value => changeProperty(value, 'email')}
                      text={userDetails.email}
                      isDisabled={isEdit}
                      cy="user_email_input"
                    />
                  )
                }
              ]}
            />
          </Col>
          <Col lg="6">
            <KeyValuePairs
              pairs={[
                {
                  key: '* Last Name',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <InputContainer
                      type="text"
                      placeholder="Enter Last Name"
                      cb={value => changeProperty(value, 'lastName')}
                      text={userDetails.lastName}
                      cy="user_last_name_input"
                    />
                  )
                },
                !isEdit
                  ? {
                      key: '* Password',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <InputContainer
                          type="text"
                          placeholder="Enter Password"
                          cb={value => changeProperty(value, 'password')}
                          text={userDetails.password}
                          cy="user_password_input"
                        />
                      )
                    }
                  : {}
              ]}
            />
          </Col>
        </Row>
        <p className={s.roles}>
          * Role(s): check all that apply; at least one needs to be checked{' '}
        </p>
        {userDetails.roles.map((role, index) => (
          <div key={role.value}>
            <Checkbox
              isChecked={role.checked}
              triggerCheckbox={value => changeRoles(value.target.checked, index)}
            />
            <span className={s.roleLabel}>{role.label}</span>
            <p className={s.roleDescription}>{role.description}</p>
          </div>
        ))}
        <Row>
          <Col lg="6">
            <KeyValuePairs
              pairs={[
                {
                  key: 'Teams',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'teams')}
                      options={toTeams(teams)}
                      value={userDetails.teams}
                      className={s.select}
                      id="team_select_cy"
                      isMulti
                    />
                  )
                },
                {
                  key: 'Phone',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <InputContainer
                      type="text"
                      placeholder="Enter Phone"
                      cb={value => changeProperty(value, 'phone')}
                      text={userDetails.phone}
                      classname={s.input}
                      cy="user_phone_input"
                    />
                  )
                },
                {
                  key: `Yearly Quota ${getCurrency().symbol}`,
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Yearly Quota"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.yearlyQuota}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeQuota(formattedValue.target.value, 'yearlyQuota')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                },
                {
                  key: `Yearly Target Profit ${getCurrency().symbol}`,
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Yearly Target Profit"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.yearlyTargetProfit}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeTargetProfit(formattedValue.target.value, 'yearlyTargetProfit')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                }
              ]}
            />
          </Col>
          <Col lg="6">
            <KeyValuePairs
              pairs={[
                {
                  key: 'Department',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <Select
                      onChange={value => changeProperty(value, 'department')}
                      options={toDepartments(departments)}
                      value={userDetails.department}
                      className={s.select}
                      id="department_select_cy"
                    />
                  )
                },
                {
                  key: 'Cell Phone',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <InputContainer
                      type="text"
                      placeholder="Enter Cell Phone"
                      cb={value => changeProperty(value, 'cellPhone')}
                      text={userDetails.cellPhone}
                      classname={s.input}
                      cy="user_cellphone_input"
                    />
                  )
                },
                {
                  key: `Monthly Quota ${getCurrency().symbol}`,
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Monthly Quota"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.monthlyQuota}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeQuota(formattedValue.target.value, 'monthlyQuota')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                },
                {
                  key: `Monthly Target Profit ${getCurrency().symbol}`,
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Monthly Target Profit"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.monthlyTargetProfit}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeTargetProfit(formattedValue.target.value, 'monthlyTargetProfit')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                }
              ]}
            />
          </Col>
        </Row>
        {error && <p className={s.error}>{error}</p>}
        {!isEdit && (
          <Button onClick={addUser} className={s.saveBtn} data-cy="submit_user_btn">
            {loading ? <Spinner /> : <>Create User & Send Notification Email</>}
          </Button>
        )}
        {isEdit && (
          <>
            <Button onClick={updateUser} className={s.saveBtn} data-cy="submit_user_btn">
              {loading ? <Spinner /> : <>Save Changes</>}
            </Button>
            <Button onClick={resetForm}>Cancel</Button>
          </>
        )}
      </Col>
    </div>
  )
}

export default AddUser
